/**
 * useUserSettings - 2026-06-07 settings-emergency-quotes-refresh-final.
 *
 * Single hook every settings tab can use to read + persist its own
 * section through the canonical
 *   GET  /api/v1/user/portfolio-settings
 *   PUT  /api/v1/user/portfolio-settings/{section}
 * surface.
 *
 * Section names map 1:1 to the backend allow-list:
 *   risk_limits | position_size_rules | asset_class_permissions |
 *   leverage_controls | strategy_allocation | rebalancing |
 *   trade_sync | tax_lot_prefs | analytics_prefs | ai_permissions |
 *   emergency_controls | capital_allocation | trading_profile
 *
 * The hook intentionally does NOT carry any default values. Section
 * components own their defaults; the hook only persists what they
 * commit. When the backend has no saved row for that section, the
 * payload is null and the UI renders its in-memory defaults.
 */
import { useCallback, useEffect, useState } from "react";
import { apiV1Base } from "./apiConfig";

export type SettingsSection =
  | "risk_limits"
  | "position_size_rules"
  | "asset_class_permissions"
  | "leverage_controls"
  | "strategy_allocation"
  | "rebalancing"
  | "trade_sync"
  | "tax_lot_prefs"
  | "analytics_prefs"
  | "ai_permissions"
  | "emergency_controls"
  | "capital_allocation"
  | "trading_profile";

export interface UseUserSettingsResult<T> {
  payload: T | null;
  updatedAtUtc: string | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  save: (payload: T) => Promise<{ ok: boolean; error?: string }>;
  refetch: () => Promise<void>;
}

function _token(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem("adversyn_auth_token");
}

export default function useUserSettings<T = Record<string, unknown>>(
  section: SettingsSection,
): UseUserSettingsResult<T> {
  const [payload, setPayload] = useState<T | null>(null);
  const [updatedAtUtc, setUpdatedAtUtc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    const tok = _token();
    if (!tok) {
      setLoading(false);
      setPayload(null);
      setError("not authenticated");
      return;
    }
    try {
      const r = await fetch(apiV1Base() + "/user/portfolio-settings", {
        headers: { Authorization: `Bearer ${tok}` },
        credentials: "omit",
      });
      if (!r.ok) {
        setError(`HTTP ${r.status}`);
        setPayload(null);
        setLoading(false);
        return;
      }
      const body = await r.json();
      const data = body?.data ?? body;
      const row = data?.sections?.[section];
      if (row && typeof row === "object") {
        setPayload((row.payload ?? null) as T | null);
        setUpdatedAtUtc(typeof row.updated_at_utc === "string"
          ? row.updated_at_utc : null);
      } else {
        setPayload(null);
        setUpdatedAtUtc(null);
      }
    } catch (e: any) {
      setError(String(e?.message || e));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [section]);

  useEffect(() => { void refetch(); }, [refetch]);

  const save = useCallback(async (next: T) => {
    setSaving(true);
    setError(null);
    const tok = _token();
    if (!tok) {
      setSaving(false);
      setError("not authenticated");
      return { ok: false, error: "not authenticated" };
    }
    try {
      const r = await fetch(
        apiV1Base() + "/user/portfolio-settings/" + encodeURIComponent(section),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${tok}`,
            "Content-Type": "application/json",
          },
          credentials: "omit",
          body: JSON.stringify({ payload: next }),
        },
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = body?.error?.message
          || body?.message
          || `HTTP ${r.status}`;
        setError(String(msg));
        setSaving(false);
        return { ok: false, error: String(msg) };
      }
      const data = body?.data ?? body;
      setPayload((data?.payload ?? next) as T);
      setUpdatedAtUtc(typeof data?.updated_at_utc === "string"
        ? data.updated_at_utc : null);
      setSaving(false);
      return { ok: true };
    } catch (e: any) {
      const msg = String(e?.message || e);
      setError(msg);
      setSaving(false);
      return { ok: false, error: msg };
    }
  }, [section]);

  return { payload, updatedAtUtc, loading, saving, error, save, refetch };
}
